import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import { query as i } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdBaseComponent } from '../pvd-base-component/pvd-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';

const DEFAULT_DISABLED = false;
const DEFAULT_ERROR = false;
const DEFAULT_FULL_WIDTH = false;
const DEFAULT_REQUIRED = false;
const DEFAULT_SIZE = StandardSize.medium;
const SELECT_PROTECTED_ATTR_MAP = new Map([
    ['aria-describedby', 'pvd-aria-describedby'],
    ['aria-disabled', 'pvd-disabled'],
    ['aria-invalid', 'pvd-error'],
    ['aria-required', 'pvd-required'],
    ['aria-label', 'pvd-aria-label'],
    ['disabled', 'pvd-disabled'],
    ['id', 'pvd-id'],
    ['required', 'pvd-required'],
]);
/**
 * Providence Select component.
 * @see {@link https://providence.fmr.com/4.x/components/select/select.html|Select documentation}
 */
class PvdSelect extends ScopedElementsMixin(PvdBaseComponent) {
    constructor() {
        super(...arguments);
        this.pvdDisabled = DEFAULT_DISABLED;
        this.pvdError = DEFAULT_ERROR;
        this.pvdFullWidth = DEFAULT_FULL_WIDTH;
        this.pvdRequired = DEFAULT_REQUIRED;
        this.pvdSize = DEFAULT_SIZE;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region private methods
    get iconSize() {
        return this.pvdSize === StandardSize.large ? Size.medium : Size.small;
    }
    /**
     * Sets attributes on the select element that are coming from pvdNativeselectAttributes
     * @param prevAttr optional, if user updates pvdNativeSelectAttributes, this will remove/update existing attributes
     */
    setNativeSelectAttributes(prevAttr) {
        this.setNativeAttributes(this.pvdNativeSelectAttributes, SELECT_PROTECTED_ATTR_MAP, this._nativeSelectEl, prevAttr);
    }
    /**
     * On first render, if pvdSelectedValue does not exist, set it to the option in the selectedIndex
     * Needed to correctly set `aria-label` to the selected option for proper readout on NVDA+FF
     */
    setSelectedValue() {
        this.pvdSelectedValue =
            this._nativeSelectEl.options.length > 0
                ? this._nativeSelectEl.options[this._nativeSelectEl.options.selectedIndex].value
                : undefined;
    }
    // #endregion
    // #region events
    /**
     * Update the `pvdSelectedValue` property to match the new value of the <select> element
     * @param e
     */
    onChange(e) {
        this.pvdSelectedValue = e.target.value;
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        if (!this.pvdSelectedValue) {
            this.setSelectedValue();
        }
        if (this.pvdNativeSelectAttributes) {
            const prevAttr = changedProperties.get('pvdNativeSelectAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeSelectAttributes(prevAttr);
        }
    }
    // iterates through the pvdOptions array and determines if each object is a single option or an option group
    renderSelectOptions() {
        return x `${this.pvdOptions
            ? this.pvdOptions.map((option) => x `${option.label
                ? this.renderOptGroup(option)
                : this.renderOption(option)}`)
            : ''}`;
    }
    // renders a single option group
    renderOptGroup(optGroup) {
        return x `<optgroup label="${optGroup.label}">${optGroup.options.map((option) => x `${this.renderOption(option)}`)}</optgroup>`;
    }
    // renders a single option
    renderOption(option) {
        // Checking for `option.value == undefined` allows for an empty string to be
        // provided as an option's value. If we didn't check for undefined, an empty
        // string would be replaced by `option.text`.
        const value = option.value == undefined ? option.text : option.value;
        const selected = this.pvdSelectedValue && this.pvdSelectedValue === value ? true : undefined;
        const ariaLabel = selected ? option.text : undefined;
        return x `${option.text
            ? x `<option aria-label="${l(ariaLabel)}" value="${value}" ?disabled="${l(option.disabled)}" ?selected="${l(selected)}">${option.text}</option>`
            : ''}`;
    }
    // renders the caret icon in the select
    renderSelectIcon() {
        return this.scopeTemplate `<pvd-scoped-icon
      pvd-icon-class="pvd-select__icon"
      pvd-name="action__caret-down"
      pvd-size="${this.iconSize}"
    ></pvd-scoped-icon>`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-select-root`,
            modifierPrefix: 'pvd-select',
            modifiers: [
                {
                    condition: this.pvdDisabled,
                    className: 'disabled',
                },
                {
                    condition: this.pvdError,
                    className: 'error',
                },
                {
                    condition: this.pvdFullWidth,
                    className: 'full-width',
                },
                {
                    condition: true,
                    className: `${this.pvdSize}`,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}"><div class="pvd-select__wrapper"><select @blur="${this.bubbleEvent}" @change="${this.onChange}" @focus="${this.bubbleEvent}" ?disabled="${this.pvdDisabled}" ?required="${this.pvdRequired}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-disabled="${l(this.a11yBoolean(this.pvdDisabled))}" aria-invalid="${l(this.a11yBoolean(this.pvdError))}" aria-label="${l(this.pvdAriaLabel)}" aria-required="${l(this.a11yBoolean(this.pvdRequired))}" class="pvd-select__select" id="${l(this.pvdId)}" .value="${this.pvdSelectedValue}">${this.renderSelectOptions()}</select> ${this.renderSelectIcon()}</div></div>`;
    }
}
//#region Component configuration
PvdSelect.componentName = 'select';
__decorate([
    i('.pvd-select__select'),
    __metadata("design:type", HTMLSelectElement)
], PvdSelect.prototype, "_nativeSelectEl", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-aria-describedby',
    }),
    __metadata("design:type", String)
], PvdSelect.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-label' }),
    __metadata("design:type", String)
], PvdSelect.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disabled',
        ...PvdBaseComponent.booleanAttribute('pvd-disabled'),
    }),
    __metadata("design:type", Boolean)
], PvdSelect.prototype, "pvdDisabled", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-error',
        ...PvdBaseComponent.booleanAttribute('pvd-error'),
    }),
    __metadata("design:type", Boolean)
], PvdSelect.prototype, "pvdError", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-full-width',
        ...PvdBaseComponent.booleanAttribute('pvd-full-width'),
    }),
    __metadata("design:type", Boolean)
], PvdSelect.prototype, "pvdFullWidth", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-id' }),
    __metadata("design:type", String)
], PvdSelect.prototype, "pvdId", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-select-attributes' }),
    __metadata("design:type", Object)
], PvdSelect.prototype, "pvdNativeSelectAttributes", void 0);
__decorate([
    n({ type: Array, reflect: true, attribute: 'pvd-options' }),
    __metadata("design:type", Array)
], PvdSelect.prototype, "pvdOptions", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-required',
        ...PvdBaseComponent.booleanAttribute('pvd-required'),
    }),
    __metadata("design:type", Boolean)
], PvdSelect.prototype, "pvdRequired", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-selected-value',
    }),
    __metadata("design:type", String)
], PvdSelect.prototype, "pvdSelectedValue", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdSelect.prototype, "pvdSize", void 0);

export { DEFAULT_DISABLED, DEFAULT_ERROR, DEFAULT_FULL_WIDTH, DEFAULT_REQUIRED, DEFAULT_SIZE, PvdSelect, SELECT_PROTECTED_ATTR_MAP };
//# sourceMappingURL=pvd-select.js.map
